<template>
  <div class="page-view">
    <BasePageHeader :title="$t('branding.title')" @back="$router.back()">
      <template #actions>
        <router-link to="/admin/branding/contract-template" class="btn ghost small">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14,2 14,8 20,8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10,9 9,9 8,9"/>
          </svg>
          {{ $t('branding.contractTemplate') }}
        </router-link>
      </template>
    </BasePageHeader>

    <!-- Loading -->
    <div v-if="loading" class="px-5 pt-4 flex flex-col gap-4 animate-pulse">
      <div class="h-6 w-1/4 bg-[var(--color-surface)] rounded"></div>
      <div class="h-32 bg-[var(--color-surface)] rounded-xl"></div>
      <div class="h-6 w-1/4 bg-[var(--color-surface)] rounded mt-2"></div>
      <div class="h-11 bg-[var(--color-surface)] rounded-xl"></div>
      <div class="h-11 bg-[var(--color-surface)] rounded-xl"></div>
    </div>

    <!-- Error -->
    <EmptyState v-else-if="loadError" :message="loadError" variant="error" />

    <!-- Form -->
    <form v-else class="px-4 pt-4 pb-10 flex flex-col gap-6" @submit.prevent="save">

      <!-- Info banner -->
      <div class="flex items-start gap-3 px-4 py-3 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl text-sm text-[var(--color-muted)]">
        <svg class="flex-shrink-0 mt-0.5 text-[var(--color-accent)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="16" x2="12" y2="12"/>
          <line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
        {{ $t('branding.infoBanner') }}
      </div>

      <!-- Logo -->
      <div class="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden">
        <div class="px-4 py-3 border-b border-[var(--color-border)]">
          <div class="section-label">{{ $t('branding.logoSection') }}</div>
        </div>
        <div class="px-4 py-4 flex flex-col gap-4">
          <div class="flex items-center gap-5">
            <div class="w-24 h-16 rounded-lg border border-[var(--color-border)] flex items-center justify-center overflow-hidden bg-[var(--color-surface-2)] flex-shrink-0">
              <img v-if="logoPreview || branding.logo_url" :src="logoPreview || branding.logo_url" :alt="$t('ui.logoPreview')" class="max-w-full max-h-full object-contain" />
              <span v-else class="text-[var(--color-muted)] text-xs">{{ $t('branding.noLogo') }}</span>
            </div>
            <div class="flex flex-col gap-2 flex-1 min-w-0">
              <BaseFormField :label="$t('branding.uploadLogoLabel')" :hint="$t('branding.logoHint')">
                <div class="flex gap-2 flex-wrap">
                  <label class="btn ghost small relative max-w-full overflow-hidden cursor-pointer focus-within:outline-2 focus-within:outline-[var(--color-focus-ring)] focus-within:outline-offset-2">
                    <span>{{ $t('branding.chooseFile') }}</span>
                    <BaseInput
                      type="file"
                      class="absolute inset-0 !w-full !h-full opacity-0 cursor-pointer"
                      accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      :aria-label="$t('branding.uploadLogoLabel')"
                      @change="onLogoFileChange"
                    />
                  </label>
                  <p v-if="pendingLogoFile" class="form-hint break-all w-full">{{ pendingLogoFile.name }}</p>
                  <button v-if="pendingLogoFile" type="button" class="btn danger small" @click="clearLogo">{{ $t('common.remove') }}</button>
                </div>
              </BaseFormField>
            </div>
          </div>

          <div v-if="logoUploadError" class="state-empty error text-sm py-2">{{ logoUploadError }}</div>

          <div class="flex justify-end">
            <button type="button" class="btn primary small" :disabled="!pendingLogoFile || savingLogo" @click="saveLogo">
              <svg v-if="savingLogo" class="btn-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
              </svg>
              {{ savingLogo ? $t('branding.uploading') : $t('branding.uploadLogoBtn') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Company Info -->
      <div class="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden">
        <div class="px-4 py-3 border-b border-[var(--color-border)]">
          <div class="section-label">{{ $t('branding.companyInfoSection') }}</div>
        </div>
        <div class="px-4 py-4 flex flex-col gap-4">
          <BaseFormField :label="$t('branding.companyNameLabel')" :hint="$t('branding.companyNameHint')" :error="fieldErrors.company_name">
            <BaseInput v-model="form.company_name" :placeholder="$t('ui.acmeRentalsAs')" maxlength="200" />
          </BaseFormField>
          <div class="form-row">
            <BaseFormField :label="$t('branding.orgNumberLabel')" :hint="$t('branding.orgNumberHint')" :error="fieldErrors.org_number">
              <BaseInput v-model="form.org_number" placeholder="987 654 321" maxlength="30" />
            </BaseFormField>
            <BaseFormField :label="$t('branding.vatRegistered')" class="flex flex-col justify-end">
              <BaseCheckbox v-model="form.vat_registered" :label="$t('branding.vatRegistered')" />
            </BaseFormField>
          </div>
          <BaseFormField :label="$t('branding.bankAccountLabel')" :error="fieldErrors.bank_account">
            <BaseInput v-model="form.bank_account" placeholder="1234.56.78901" maxlength="40" />
          </BaseFormField>
        </div>
      </div>

      <!-- Contact -->
      <div class="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden">
        <div class="px-4 py-3 border-b border-[var(--color-border)]">
          <div class="section-label">{{ $t('branding.contactSection') }}</div>
        </div>
        <div class="px-4 py-4 flex flex-col gap-4">
          <div class="form-row">
            <BaseFormField :label="$t('branding.emailLabel')" :error="fieldErrors.email">
              <BaseInput v-model="form.email" type="email" :placeholder="$t('ui.postExampleNo')" maxlength="254" />
            </BaseFormField>
            <BaseFormField :label="$t('branding.phoneLabel')" :error="fieldErrors.phone">
              <BaseInput v-model="form.phone" type="tel" placeholder="+47 22 00 00 00" maxlength="50" />
            </BaseFormField>
          </div>
          <BaseFormField :label="$t('branding.websiteLabel')" :error="fieldErrors.website">
            <BaseInput v-model="form.website" type="url" :placeholder="$t('ui.httpsExampleNo')" maxlength="200" />
          </BaseFormField>
        </div>
      </div>

      <!-- Address -->
      <div class="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden">
        <div class="px-4 py-3 border-b border-[var(--color-border)]">
          <div class="section-label">{{ $t('branding.addressSection') }}</div>
        </div>
        <div class="px-4 py-4 flex flex-col gap-4">
          <BaseFormField :label="$t('branding.addressLine1Label')" :error="fieldErrors.address_line1">
            <BaseInput v-model="form.address_line1" placeholder="Storgata 1" maxlength="200" />
          </BaseFormField>
          <BaseFormField :label="$t('branding.addressLine2Label')" :error="fieldErrors.address_line2">
            <BaseInput v-model="form.address_line2" :placeholder="$t('ui.cOSomeone')" maxlength="200" />
          </BaseFormField>
          <div class="form-row">
            <BaseFormField :label="$t('branding.postalCodeLabel')" :error="fieldErrors.postal_code">
              <BaseInput v-model="form.postal_code" placeholder="0150" maxlength="10" />
            </BaseFormField>
            <BaseFormField :label="$t('branding.cityLabel')" :error="fieldErrors.city">
              <BaseInput v-model="form.city" placeholder="Oslo" maxlength="100" />
            </BaseFormField>
          </div>
          <BaseFormField :label="$t('branding.countryLabel')" :error="fieldErrors.country">
            <BaseInput v-model="form.country" :placeholder="$t('ui.norway')" maxlength="100" />
          </BaseFormField>
        </div>
      </div>

      <!-- Colors -->
      <div class="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden">
        <div class="px-4 py-3 border-b border-[var(--color-border)]">
          <div class="section-label">{{ $t('branding.colorsSection') }}</div>
        </div>
        <div class="px-4 py-4 flex flex-col gap-4">
          <div class="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-4">
            <BaseFormField :label="$t('branding.primaryColor')">
              <div class="flex gap-2 items-center">
                <BaseInput type="color" v-model="form.primary_color" class="h-10 w-12 rounded cursor-pointer border border-[var(--color-border)] bg-transparent p-0.5" />
                <BaseInput v-model="form.primary_color" class="flex-1" placeholder="#1a237e" maxlength="7" />
              </div>
            </BaseFormField>
            <BaseFormField :label="$t('branding.secondaryColor')">
              <div class="flex gap-2 items-center">
                <BaseInput type="color" v-model="form.secondary_color" class="h-10 w-12 rounded cursor-pointer border border-[var(--color-border)] bg-transparent p-0.5" />
                <BaseInput v-model="form.secondary_color" class="flex-1" placeholder="#283593" maxlength="7" />
              </div>
            </BaseFormField>
            <BaseFormField :label="$t('branding.accentColor')">
              <div class="flex gap-2 items-center">
                <BaseInput type="color" v-model="form.accent_color" class="h-10 w-12 rounded cursor-pointer border border-[var(--color-border)] bg-transparent p-0.5" />
                <BaseInput v-model="form.accent_color" class="flex-1" placeholder="#3949ab" maxlength="7" />
              </div>
            </BaseFormField>
          </div>
        </div>
      </div>

      <!-- Document Defaults -->
      <div class="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden">
        <div class="px-4 py-3 border-b border-[var(--color-border)]">
          <div class="section-label">{{ $t('branding.documentDefaultsSection') }}</div>
        </div>
        <div class="px-4 py-4 flex flex-col gap-4">
          <BaseFormField :label="$t('branding.paymentTermsLabel')">
            <BaseInput v-model.number="form.payment_terms_days" type="number" min="0" placeholder="14" />
          </BaseFormField>
          <BaseFormField :label="$t('branding.offerIntroTextLabel')" :hint="$t('branding.offerIntroHint')" :error="fieldErrors.offer_intro_text">
            <BaseTextarea v-model="form.offer_intro_text" :rows="3" :placeholder="$t('ui.thankYouForYourInterest')" />
          </BaseFormField>
          <BaseFormField :label="$t('branding.offerTermsLabel')" :hint="$t('branding.offerTermsHint')" :error="fieldErrors.offer_terms">
            <BaseTextarea v-model="form.offer_terms" :rows="4" :placeholder="$t('ui.allRentalsAreSubjectTo')" />
          </BaseFormField>
          <BaseFormField :label="$t('branding.pickingListNotesLabel')" :hint="$t('branding.pickingListNotesHint')" :error="fieldErrors.picking_list_notes">
            <BaseTextarea v-model="form.picking_list_notes" :rows="2" :placeholder="$t('ui.handleWithCare')" />
          </BaseFormField>
        </div>
      </div>

      <!-- Save -->
      <div v-if="saveError" class="state-empty error text-sm">{{ saveError }}</div>
      <div class="sticky bottom-0 -mx-4 px-4 py-3 flex justify-end gap-3 bg-[var(--color-bg)] border-t border-[var(--color-border)]">
        <button type="submit" class="btn primary" :disabled="saving">
          <svg v-if="saving" class="btn-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          {{ saving ? $t('common.saving') : $t('branding.saveChanges') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from '@/composables/useToast.js';
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseTextarea from '@/components/base/BaseTextarea.vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import { getBranding, updateBranding, uploadBrandingLogo } from '@/api/api.js';

const toast = useToast();
const { t } = useI18n();

const loading = ref(true);
const loadError = ref(null);
const saving = ref(false);
const saveError = ref(null);
const fieldErrors = reactive({});
const savingLogo = ref(false);
const logoUploadError = ref(null);

const branding = ref({});
const pendingLogoFile = ref(null);
const logoPreview = ref(null);

const form = reactive({
  company_name: '',
  org_number: '',
  vat_registered: false,
  bank_account: '',
  address_line1: '',
  address_line2: '',
  postal_code: '',
  city: '',
  country: '',
  phone: '',
  email: '',
  website: '',
  primary_color: '',
  secondary_color: '',
  accent_color: '',
  payment_terms_days: '',
  offer_intro_text: '',
  offer_terms: '',
  picking_list_notes: '',
});

const FORM_FIELDS = Object.keys(form);

async function load() {
  loading.value = true;
  loadError.value = null;
  try {
    const data = await getBranding();
    branding.value = data ?? {};
    for (const key of FORM_FIELDS) {
      form[key] = data?.[key] ?? (key === 'vat_registered' ? false : '');
    }
  } catch (e) {
    loadError.value = e?.data?.detail ?? e?.message ?? t('branding.loadFailed');
  } finally {
    loading.value = false;
  }
}

function onLogoFileChange(e) {
  const file = e.target.files?.[0];
  if (!file) return;
  if (file.size > 2 * 1024 * 1024) {
    logoUploadError.value = t('branding.logoTooLarge');
    return;
  }
  logoUploadError.value = null;
  pendingLogoFile.value = file;
  logoPreview.value = URL.createObjectURL(file);
}

function clearLogo() {
  pendingLogoFile.value = null;
  if (logoPreview.value) {
    URL.revokeObjectURL(logoPreview.value);
    logoPreview.value = null;
  }
  logoUploadError.value = null;
}

async function saveLogo() {
  if (!pendingLogoFile.value) return;
  savingLogo.value = true;
  logoUploadError.value = null;
  try {
    const data = await uploadBrandingLogo(pendingLogoFile.value);
    branding.value = data ?? branding.value;
    clearLogo();
    toast.success(t('branding.logoUpdated'));
  } catch (e) {
    logoUploadError.value = e?.data?.logo?.[0] ?? e?.data?.detail ?? e?.message ?? t('branding.uploadLogoFailed');
  } finally {
    savingLogo.value = false;
  }
}

async function save() {
  saving.value = true;
  saveError.value = null;
  Object.keys(fieldErrors).forEach(k => delete fieldErrors[k]);
  try {
    const payload = {};
    for (const key of FORM_FIELDS) {
      // send empty string rather than null — API rejects null for text fields
      payload[key] = form[key] === null ? '' : form[key];
    }
    await updateBranding(payload);
    toast.success(t('branding.saved'));
  } catch (e) {
    const d = e?.data;
    if (d && typeof d === 'object' && !d.detail) {
      // distribute field errors
      for (const [key, val] of Object.entries(d)) {
        if (key === 'status_code') continue;
        fieldErrors[key] = Array.isArray(val) ? val.join(' ') : String(val);
      }
      saveError.value = t('branding.fixErrors');
    } else {
      saveError.value = d?.detail ?? e?.message ?? t('branding.saveFailed');
    }
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>
